/**
 * PATH: packages/email-hub/src/feature/components/rich-text/snippetSlash.ts
 * FILE_ID: EMAILHUB-RICHTEXT-SNIPPETSLASH
 * PURPOSE: Map saved compose snippets → "/" slash-menu items that insert the
 *          snippet HTML at the cursor, plus the "Save as snippet" / "Manage
 *          snippets" entries.
 * WHY: Keeps slash.tsx generic — the dynamic, user-defined items are produced
 *      here and merged in by RichTextEditor via a live getter.
 * FLOW: snippets[] → SlashItem[] (run: deleteRange → insertContent(html)).
 * DEPENDENCIES:
 *   - @tiptap/core: Editor/Range types
 *   - lucide-react: icons
 *   - ./slash: SlashItem/SlashAction types
 *   - ./snippetStore: ComposeSnippet
 */
import type { Editor, Range } from '@tiptap/core'
import { FileText, Save, Settings2 } from 'lucide-react'

import type { SlashItem } from './slash'
import type { ComposeSnippet } from './snippetStore'

/** Short single-line preview of a snippet body for the menu hint. */
export function snippetHint(html: string, max = 60): string {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()
  if (!text) return 'Insert saved block'
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/**
 * Build the snippet section of the slash menu: one insert item per saved
 * snippet, then a divider pair of "Save as snippet" + "Manage snippets" that
 * delegate to the host via SlashActions.
 */
export function buildSnippetSlashItems(
  snippets: ComposeSnippet[],
): SlashItem[] {
  const inserts: SlashItem[] = snippets.map((s) => ({
    id: `snippet-${s.id}`,
    title: s.title,
    hint: snippetHint(s.html),
    icon: FileText,
    keywords: ['snippet', 'template', 'block', ...s.title.toLowerCase().split(/\s+/).filter(Boolean)],
    run: (editor: Editor, range: Range) => {
      editor.chain().focus().deleteRange(range).insertContent(s.html).run()
    },
  }))

  const manage: SlashItem[] = [
    {
      id: 'snippet-save',
      title: 'Save as snippet',
      hint: 'Reuse this block later',
      icon: Save,
      keywords: ['snippet', 'template', 'save', 'new', 'block'],
      action: 'snippet-save',
    },
    {
      id: 'snippet-manage',
      title: 'Manage snippets',
      hint: 'Rename or delete saved blocks',
      icon: Settings2,
      keywords: ['snippet', 'template', 'manage', 'delete', 'edit'],
      action: 'snippet-manage',
    },
  ]

  return [...inserts, ...manage]
}
